import React, { useRef, useState } from "react";
import SubHeader from "../customComponents/SubHeader";
import { Autocomplete, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { showAlert } from "../../redux/alertSlice";
import api from "../../utils/axiosInstance";
import moment from "moment";
import GroupedStockReportTable from "../customComponents/tables/GroupedStockReportTable";

const tableConfig = [
  {
    key: "itemName",
    label: "Item Name",
    width: "35%",
    align: "left",
    dataKey: "itemName",
  },
  {
    key: "headName",
    label: "Head Name",
    width: "35%",
    align: "left",
    dataKey: "headName",
  },
  {
    key: "required",
    label: "Required.",
    width: "15%",
    align: "right",
    dataKey: "required",
  },
  {
    key: "available",
    label: "Available",
    width: "15%",
    align: "right",
    dataKey: "available",
  },
];
const dummyReportData = {
  tableData: [
    {
      itemName: "Paracetamol 500mg",
      headName: "Pain Relief",
      required: 150,
      available: 120,
    },
    {
      itemName: "Amoxicillin 250mg",
      headName: "Antibiotics",
      required: 100,
      available: 80,
    },
    {
      itemName: "Vitamin C 500mg",
      headName: "Supplements",
      required: 200,
      available: 220,
    },
    {
      itemName: "Cough Syrup",
      headName: "Cold & Flu",
      required: 75,
      available: 50,
    },

    // ----- 10 More Items -----

    {
      itemName: "Ibuprofen 400mg",
      headName: "Pain Relief",
      required: 180,
      available: 160,
    },
    {
      itemName: "Azithromycin 500mg",
      headName: "Antibiotics",
      required: 90,
      available: 70,
    },
    {
      itemName: " ORS Sachet",
      headName: "Hydration",
      required: 300,
      available: 310,
    },
    {
      itemName: "Hand Sanitizer 100ml",
      headName: "Hygiene",
      required: 140,
      available: 200,
    },
    {
      itemName: "Bandages (Pack)",
      headName: "First Aid",
      required: 60,
      available: 55,
    },
    {
      itemName: "Thermometer Digital",
      headName: "Instruments",
      required: 25,
      available: 18,
    },
    {
      itemName: "Antacid Tablets",
      headName: "Digestion",
      required: 220,
      available: 180,
    },
    {
      itemName: "Dettol Liquid 250ml",
      headName: "Hygiene",
      required: 90,
      available: 95,
    },
    {
      itemName: "Cotton Roll 500g",
      headName: "Medical Supplies",
      required: 40,
      available: 35,
    },
    {
      itemName: "Gauze Pads (Pack)",
      headName: "Medical Supplies",
      required: 70,
      available: 65,
    },
  ],

  totals: {
    required: 1640, // sum of all required
    available: 1553, // sum of all available
  },
};


const GroupedStockReport = ({ toggleMobileMenu }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [data, setData] = useState([]);

  const [inputValue, setInputValue] = useState("");
  const [reportData, setReportData] = useState(dummyReportData); // option array coming from searching

  const searchBoxRef = useRef();
  const debounceTimeout = useRef(null); // Ref to store the timeout

  // Updated formData with new filters
  const [formData, setFormData] = useState({
    asOnDate: moment(new Date()).format("YYYY-MM-DD"),
    type: "", // "G" or "D"
    branch: "", // Branch Name
  });

  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // Example getCustomerList API (for customer autocomplete search)
  const getCustomerList = async (value) => {
    try {
      const response = await api.get(
        `/customers/get-all-customers?search=${value}`
      );
      if (response.status === 200) {
        const list = response.data;
      }
    } catch (error) {
      console.log("Error while loading customers " + error);
    }
  };

  const getCustomerData = async () => {
    try {
      setLoading(true);
      // Send API request with the three filters
      const response = await api.post("/stocks/get-grouped-stock", {
        asOnDate: formData.asOnDate,
        type: formData.type,
        branch: formData.branch,
      });

      if (response.data?.success) {
        setStatement(response.data.data);
      } else {
        setStatement([]);
        dispatch(
          showAlert({
            message: response.data.message || "No data found",
            severity: "warning",
          })
        );
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      dispatch(
        showAlert({ message: "Error fetching data", severity: "error" })
      );
    }
  };

  const handleSearch = (e) => {
    const { value } = e.target;
    if (value && value !== "") {
      setInputValue(value);

      // Debouncing logic
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        getCustomerList(value);
      }, 1000); // 1-second delay
    }
  };

  //   // Optional helper function to reset filters if you want to add that later
  //   const handleClearForm = () => {
  //     setFormData({
  //       asOnDate: moment(new Date()).format("YYYY-MM-DD"),
  //       type: "",
  //       branch: "",
  //     });
  //     setStatement([]);
  //     setSelectedValue(null);
  //     setInputValue("");
  //   };

  return (
    <>
      <SubHeader
        text={"Grouped Stock Report"}
        toggleMobileMenu={toggleMobileMenu}
      />
      <div className="w-full max-w-[1100px] mx-auto px-4 lg:px-0">
        <div className="relative w-full lg:min-h-[550px] rounded-[10px] mt-8 mb-2 shadow-custom-dark">
          <div className="w-full py-4 lg:pt-6 px-4 lg:px-8 ">
            <div>
              <div className="font-mont font-semibold pb-2">
                Filters
              </div>

              {/* Filters Row */}
              <div className="flex flex-col lg:flex-row px-0 lg:px-2 gap-4 lg:gap-6 lg:items-end mt-4 lg:mt-0">
                {/* As on Date */}
                <TextField
                  sx={{ width: { xs: "100%", lg: "30%" } }}
                  value={formData.asOnDate}
                  label="As on Date"
                  variant="standard"
                  type="date"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      asOnDate: e.target.value,
                    }))
                  }
                />

                {/* Type (G/D) */}
                <TextField
                  select
                  label="Type"
                  variant="standard"
                  SelectProps={{ native: true }}
                  sx={{ width: { xs: "100%", lg: "20%" } }}
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, type: e.target.value }))
                  }
                >
                  <option value=""></option>
                  <option value="G">G</option>
                  <option value="D">D</option>
                </TextField>

                {/* Branch Name */}
                <TextField
                  label="Branch Name"
                  variant="standard"
                  sx={{ width: { xs: "100%", lg: "30%" } }}
                  value={formData.branch}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, branch: e.target.value }))
                  }
                />
              <div className="flex justify-start mt-6">
                <button
                  className="w-full lg:w-auto py-2 px-3 border bg-sixth text-white text-sm font-mont font-semibold rounded-[5px] hover:bg-sixth/80"
                  disabled={loading}
                  onClick={getCustomerData}
                >
                  {loading ? "Loading..." : "Get Data"}
                </button>
              </div>
              </div>

 
            </div>
          </div>

          {/* Results Table */}
          <div className="w-full px-4 lg:px-7 pb-6">
            <GroupedStockReportTable
              tableData={reportData ? reportData.tableData : []}
              totals={reportData ? reportData.totals : {}}
              loading={false}
              columns={tableConfig}
              searchFields={["itemName", "headName"]}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupedStockReport;
